/* eslint-disable react-hooks/exhaustive-deps */
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Scrollbar,
  Space,
  Spinner,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableHeaderRow,
  TableRow,
  Text,
  Truncate,
  useColorMode,
} from '@tonic-one/react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { QuarantineApi } from '@Apis';
import { useOrgStore } from '../../../models/useOrgModel';
import { MailPreviewEnabledItem } from '../../../types/log';
import LoadingBtn from '../../Report/LoadingBtn';
import SmartTooltip from '../../Report/SmartTooltip';
import Note from '@Components/Common/Note';

function EnableEmailPreviewModal({
  isOpen,
  onClose,
  onUpdate,
  tip,
}: {
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (updated: { orgId: string; enabled: boolean }[]) => void;
  tip?: string;
}) {
  const { t } = useTranslation(['common', 'quarantine']);
  const [colorMode] = useColorMode();
  const hoverBackgroundColor = {
    dark: 'rgba(255, 255, 255, 0.12)',
    light: 'rgba(0, 0, 0, 0.12)',
  }[colorMode];
  const { orgsMap, orgId } = useOrgStore();
  const [tableLoading, setTableLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [originData, setOriginData] = useState<MailPreviewEnabledItem[]>([]);
  const [updateIndex, setUpdateIndex] = useState<Set<number>>(
    new Set<number>()
  );

  const onSave = async () => {
    setBtnLoading(true);
    const postData: MailPreviewEnabledItem[] = [];
    updateIndex.forEach((index) => {
      postData.push({
        orgId: originData[index].orgId,
        enabled: !originData[index].enabled,
      });
    });
    let updated = false;

    try {
      const res = await QuarantineApi.apiUpdateMailPreviewEnabledList(postData);
      if (res.data.status === 'success') updated = true;
      await fetchData();
    } catch (error) {
      console.log(JSON.stringify(error));
    } finally {
      setBtnLoading(false);
      onUpdate?.(updated ? postData : []);
      onClose();
    }
  };

  const columns: ColumnDef<MailPreviewEnabledItem>[] = useMemo(() => {
    return [
      {
        id: 'orgName',
        header: t('quarantine.modal.enableEmailPreview.table.org'),
        cell: ({ row }) => orgsMap.get(row.original.orgId) || '',
        meta: {
          customColumnStyle: {
            width: '70%',
          },
        },
      },
      {
        id: 'enabled',
        header: t('quarantine.modal.enableEmailPreview.enable'),
        cell: ({ row }) => {
          const { enabled } = row.original;
          const checked = updateIndex.has(row.index) ? !enabled : enabled;
          return (
            <Checkbox
              checked={checked}
              onChange={() => {
                const copySet = new Set(updateIndex);
                if (updateIndex.has(row.index)) {
                  copySet.delete(row.index);
                  setUpdateIndex(copySet);
                } else {
                  copySet.add(row.index);
                  setUpdateIndex(copySet);
                }
              }}
            />
          );
        },
        meta: {
          customColumnStyle: {
            width: '30%',
          },
        },
      },
    ];
  }, [orgsMap, updateIndex]);

  const table = useReactTable({
    data: originData,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  const fetchData = async () => {
    setTableLoading(true);
    try {
      const res = await QuarantineApi.apiGetMailPreviewEnabledList();
      setOriginData(res.data);
      setUpdateIndex(new Set());
    } catch (error) {
      console.error(JSON.stringify(error));
      setOriginData([]);
      setUpdateIndex(new Set());
    } finally {
      setTableLoading(false);
    }
  };

  const resetData = () => {
    setOriginData([]);
    setUpdateIndex(new Set());
    setTableLoading(false);
  };

  //reload data when open modal
  useEffect(() => {
    if (isOpen) {
      fetchData();
    } else {
      resetData();
    }
  }, [isOpen]);

  const layout = 'flexbox'; // One of: 'flexbox', 'table'

  return (
    <Modal
      closeOnEsc
      closeOnOutsideClick
      isClosable
      isOpen={isOpen}
      onClose={onClose}
      size="md"
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <Text>{t('quarantine.modal.enableEmailPreview.title')}</Text>
        </ModalHeader>
        <ModalBody>
          <Text>{t('quarantine.modal.enableEmailPreview.desc')}</Text>
          <br />
          <Note content={t('quarantine.modal.enableEmailPreview.note')} />
          {orgId === 'all' ? (
            <Table layout={layout} height="40vh" width="100%">
              <TableHeader>
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableHeaderRow key={headerGroup.id} width="100%">
                    {headerGroup.headers.map((header) => {
                      const styleProps = {
                        lineHeight: '40px',
                        minWidth: header.column.columnDef.minSize,
                        width: header.getSize(),
                        ...header.column.columnDef.meta?.customColumnStyle,
                      };
                      return (
                        <TableHeaderCell key={header.id} {...styleProps}>
                          {header.isPlaceholder ? null : (
                            <SmartTooltip>
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                            </SmartTooltip>
                          )}
                        </TableHeaderCell>
                      );
                    })}
                  </TableHeaderRow>
                ))}
              </TableHeader>
              {tableLoading ? (
                <Spinner margin="auto" />
              ) : (
                <Scrollbar height="60vh" width="100%" overflow="scroll">
                  <TableBody>
                    {table.getRowModel().rows.map((row) => (
                      <TableRow
                        width="100%"
                        key={row.id}
                        _hover={{
                          backgroundColor: hoverBackgroundColor,
                        }}
                      >
                        {row.getVisibleCells().map((cell) => {
                          const styleProps = {
                            minWidth: cell.column.columnDef.minSize,
                            width: cell.column.getSize(),
                            ...cell.column.columnDef.meta?.customColumnStyle,
                          };
                          return (
                            <TableCell key={cell.id} {...styleProps}>
                              <Truncate>
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </Truncate>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Scrollbar>
              )}
            </Table>
          ) : (
            originData.length > 0 && (
              <Checkbox
                checked={
                  updateIndex.has(0)
                    ? !originData[0].enabled
                    : originData[0].enabled
                }
                onClick={() => {
                  if (updateIndex.has(0)) {
                    setUpdateIndex(new Set());
                  } else {
                    setUpdateIndex(new Set([0]));
                  }
                }}
                pt="20px"
              >
                {t('quarantine.modal.enableEmailPreview.enable')}
              </Checkbox>
            )
          )}
          <Space height="20px" display="block" />
          {!!tip && <Text>{tip}</Text>}
        </ModalBody>
        <ModalFooter>
          <LoadingBtn loading={btnLoading} onClick={onSave}>
            {t('common.button.save')}
          </LoadingBtn>
          <Space width="8px" />
          <Button onClick={onClose}>{t('common.button.cancel')}</Button>
        </ModalFooter>
        <ModalCloseButton data-test="modal-close-button" />
      </ModalContent>
    </Modal>
  );
}
export default EnableEmailPreviewModal;
